/**
 * Zeal Genesis - Astrology API Client
 */
import authFetch from './authFetch';

export const API_BASE = 'https://saxssup4th.execute-api.us-east-1.amazonaws.com';

export interface ChartPlanet {
  longitude: number;
  sign: string;
  degree: number;
  element: string;
  house: number;
  retrograde: boolean;
  speed: number;
}

export interface NatalChart {
  planets: Record<string, ChartPlanet>;
  ascendant: { sign: string; degree: number | null; longitude: number | null; reliable?: boolean };
  midheaven: { sign: string; degree: number | null; longitude: number | null; reliable?: boolean };
  houses: Record<string, number>;
  aspects: Array<{
    planet_a: string;
    planet_b: string;
    type: string;
    angle: number;
    orb: number;
  }>;
  element_distribution: Record<string, number>;
  dominant_element: string;
  sun_sign: string;
  moon_sign: string;
  rising_sign: string;
  north_node_sign?: string;
  south_node_sign?: string;
  midheaven_sign?: string;
  moon_sign_uncertain?: boolean;
  time_unknown: boolean;
}

export interface DominantInfluence {
  planet: string;
  role: 'chart_ruler' | 'strongest_other' | 'second_strongest_other' | 'fourth_influence' |
        'strongest' | 'second_strongest' | 'third_strongest';
  score: number;
  rank: number;
  sign: string;
  house?: number | null;
  structural: boolean;
  reasons: string[];
}

export interface ZealProfile {
  name?: string;
  natal_scores: Record<string, number>;
  question_scores: Record<string, number>;
  final_attributes: Record<string, number>;
  score_bands: Record<string, string>;
  relational_axes: Record<string, number>;
  archetype: string;
  birth_archetype?: string;
  archetype_locked?: boolean;
  guiding_planet?: string;
  chart_ruler?: string | null;
  outer_co_ruler?: string | null;
  dominant_influences?: DominantInfluence[];
  archetype_time_path?: 'timed' | 'unknown_time';
  sub_archetype?: string;
  sub_archetype_variant?: string;
  sub_archetype_triad?: string[];
  archetype_distance: number;
  archetype_distances: Record<string, number>;
  cps: number;
  cps_tier?: string;
  cps_max?: number;
  q13_path: string;
  north_node_sign?: string;
  south_node_sign?: string;
  sun_sign: string;
  moon_sign: string;
  rising_sign: string;
  chart?: NatalChart;
  chart_summary: {
    sun_sign: string;
    moon_sign: string;
    rising_sign: string;
    north_node_sign?: string;
    south_node_sign?: string;
    dominant_element: string;
    element_distribution: Record<string, number>;
    element_weighted?: Record<string, number>;
    midheaven_sign?: string;
    moon_sign_uncertain?: boolean;
    guiding_planet?: string;
    sect?: { type: 'day' | 'night'; luminary: 'Sun' | 'Moon'; sun_altitude: number } | null;
    solar_conditions?: Record<string, { condition: 'cazimi' | 'combust' | 'under_beams' | 'free_of_beams'; phase: 'morning' | 'evening'; separation: number }>;
    lots?: Record<'fortune' | 'spirit', { longitude: number; sign: string; degree: number; element: string; house: number }> | Record<string, never>;
    natal_decans?: Record<string, { sign: string; number: 1 | 2 | 3; ruler: string; degree: number; theme: string; system: 'Chaldean' }>;
    fixed_star_contacts?: Array<{ star: string; point: string; separation: number; orb: number; longitude: number; sign: string; degree: number; nature: string; meaning: string; method: string }>;
    time_unknown: boolean;
  };
  spiritual_path?: string;
  soul_essence?: number;
  unlocked_oracle_images?: string[];
  active_oracle_image?: string;
}

export async function calculateChart(
  birthDate: string,
  birthTime: string,
  latitude: number,
  longitude: number,
  timeUnknown: boolean = false,
): Promise<NatalChart> {
  const res = await authFetch(`${API_BASE}/chart`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      birth_date: birthDate,
      birth_time: birthTime,
      latitude,
      longitude,
      time_unknown: timeUnknown,
    }),
  });

  if (!res.ok) throw new Error(`Chart API error: ${res.status}`);
  const data = await res.json();
  return data.chart;
}

export async function calculateProfile(
  birthDate: string,
  birthTime: string,
  latitude: number,
  longitude: number,
  answers: Record<number, string>,
  timeUnknown: boolean = false,
  userId?: string,
  userName?: string,
  birthPlace?: string,
  gender?: string,
  genderCustom?: string,
  spiritualPath?: string,
): Promise<ZealProfile> {
  const answersObj: Record<string, string> = {};
  for (const [k, v] of Object.entries(answers)) {
    answersObj[String(k)] = v;
  }

  const res = await authFetch(`${API_BASE}/profile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      birth_date: birthDate,
      birth_time: birthTime,
      latitude,
      longitude,
      time_unknown: timeUnknown,
      answers: answersObj,
      user_id: userId || '',
      name: userName || '',
      birth_place: birthPlace || '',
      gender: gender || '',
      gender_custom: genderCustom || '',
      spiritual_path: spiritualPath || '',
    }),
  });

  if (!res.ok) throw new Error(`Profile API error: ${res.status}`);
  const data = await res.json();
  return data.profile;
}

export interface AvatarResult {
  image_url: string;
  revised_prompt: string;
  prompt: string;
  provider?: string;
  model?: string;
  quality?: string;
  generations_remaining?: number;
}

export async function generateAvatar(
  form: string,
  aesthetic: string,
  subType: string,
  profile: ZealProfile,
  userId?: string,
): Promise<AvatarResult> {
  const res = await authFetch(`${API_BASE}/avatar`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      form,
      aesthetic,
      sub_type: subType,
      profile,
      user_id: userId || '',
      async_supported: true,
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Avatar API error: ${res.status}`);
  }
  const data = await res.json();
  if (data.avatar) return data.avatar;
  if (!data.job_id) throw new Error('Avatar generation did not start');

  const deadline = Date.now() + 210_000;
  while (Date.now() < deadline) {
    await new Promise(resolve => setTimeout(resolve, 3000));
    const statusRes = await authFetch(`${API_BASE}/avatar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'status',
        job_id: data.job_id,
        user_id: userId || '',
      }),
    });
    const status = await statusRes.json().catch(() => ({}));
    if (!statusRes.ok) throw new Error(status.error || `Avatar status error: ${statusRes.status}`);
    if (status.status === 'completed' && status.avatar) return status.avatar;
    if (status.status === 'failed') throw new Error(status.error || 'Avatar generation failed');
  }
  throw new Error('Avatar generation is taking longer than expected. Please try again.');
}

export interface PublicProfile {
  name: string;
  archetype: string;
  cps: number;
  cps_tier?: string;
  cps_max?: number;
  final_attributes: Record<string, number>;
  score_bands: Record<string, string>;
  relational_axes: Record<string, number>;
  q13_path: string;
  sun_sign: string;
  moon_sign: string;
  rising_sign: string;
  dominant_element: string;
  avatar_url: string;
  avatar_form: string;
  avatar_aesthetic: string;
}

export async function getPublicProfile(userId: string): Promise<PublicProfile | null> {
  const res = await authFetch(`${API_BASE}/public-profile?user_id=${encodeURIComponent(userId)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Public profile API error: ${res.status}`);
  const data = await res.json();
  return data.profile;
}

export interface TarotCard {
  id: number;
  name: string;
  suit: string;
  primary: string;
  secondary: string;
  core_power: string;
  light: string;
  shadow: string;
  quest: string;
  quest_light?: string;
  quest_deep?: string;
  points: number;
  numeral?: string;
  planet?: string;
  element?: string;
}

export interface TarotPull {
  card: TarotCard;
  interpretation: string;
  date: string;
  already_pulled: boolean;
  quest_completed: boolean;
  quest_difficulty?: string;
  quest_completed_at?: number;
  pulled_at?: number;
  is_reversed?: boolean;
}

export async function pullTarot(userId: string, allowReversals: boolean = false, spread: boolean = false): Promise<TarotPull> {
  const res = await authFetch(`${API_BASE}/tarot/pull`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, allow_reversals: allowReversals, spread }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Tarot API error: ${res.status}`);
  }
  const data = await res.json();
  return data.tarot;
}

export async function getTarotToday(userId: string): Promise<TarotPull | null> {
  const res = await authFetch(`${API_BASE}/tarot/today?user_id=${encodeURIComponent(userId)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error(`Tarot API error: ${res.status}`);
  const data = await res.json();
  return data.tarot;
}

export async function acceptMoonInvitation(userId: string, primary: string, secondary: string, themeQuote?: string): Promise<any> {
  const res = await authFetch(`${API_BASE}/moon/invitation-complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, action: 'accept', primary, secondary, theme_quote: themeQuote }),
  });
  return res.json();
}

export async function completeMoonInvitation(userId: string, primary: string, secondary: string, duration?: number, reflectionText?: string, themeQuote?: string): Promise<any> {
  const res = await authFetch(`${API_BASE}/moon/invitation-complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, action: 'complete', primary, secondary, duration: duration || 5, reflection_text: reflectionText, theme_quote: themeQuote }),
  });
  return res.json();
}

export async function getMoonInvitationStatus(userId: string): Promise<{ completed: boolean; accepted_at: number; completed_at: number }> {
  const res = await authFetch(`${API_BASE}/moon/invitation-status?user_id=${encodeURIComponent(userId)}`);
  return res.json();
}

export async function completeTarotQuest(userId: string, date: string, primary?: string, secondary?: string, reflectionText?: string, difficulty?: string, cardName?: string, questPrompt?: string): Promise<any> {
  const res = await authFetch(`${API_BASE}/tarot/quest-complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, date, primary, secondary, reflection_text: reflectionText, difficulty: difficulty || 'medium', card_name: cardName, quest_prompt: questPrompt }),
  });
  return res.json();
}

export async function getProfile(userId: string): Promise<ZealProfile | null> {
  const res = await authFetch(`${API_BASE}/profile?user_id=${encodeURIComponent(userId)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Profile API error: ${res.status}`);
  const data = await res.json();
  return data.profile;
}

/**
 * Update a single profile setting in DynamoDB (e.g. spiritual_path).
 */
export async function updateProfileSetting(userId: string, field: string, value: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/profile/update-setting`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, field, value }),
  });
  if (!res.ok) {
    console.warn(`Failed to update profile setting ${field}:`, res.status);
  }
}

/**
 * Check if a user has a profile WITHOUT requiring auth tokens.
 * Uses the public-profile endpoint (no auth needed).
 * Returns the public profile if it exists, null otherwise.
 */
export async function checkProfileExists(userId: string): Promise<any | null> {
  try {
    const res = await fetch(`${API_BASE}/public-profile?user_id=${encodeURIComponent(userId)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (res.status === 404) return null;
    if (!res.ok) return null;
    const data = await res.json();
    return data.profile || null;
  } catch {
    return null;
  }
}

// ═══════════════════════════════════════════
//  ORACLE API
// ═══════════════════════════════════════════

export interface OracleChatResponse {
  response: string;
  conversation_id: string;
  message_count: number;
  growth?: Array<{ attribute: string; old_score: number; new_score: number }>;
}

export async function oracleChat(
  userId: string,
  message: string,
  conversationId?: string,
  mode: string = 'open',
  context: string = '',
): Promise<OracleChatResponse> {
  const res = await authFetch(`${API_BASE}/oracle/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_id: userId,
      message,
      conversation_id: conversationId,
      mode,
      context,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error: any = new Error(err.error || `Oracle API error: ${res.status}`);
    error.upgrade = err.upgrade || false;
    error.status = res.status;
    throw error;
  }
  return res.json();
}

export async function getOracleIntro(userId: string): Promise<string> {
  const res = await authFetch(`${API_BASE}/oracle/intro?user_id=${encodeURIComponent(userId)}`);
  if (!res.ok) throw new Error(`Oracle intro error: ${res.status}`);
  const data = await res.json();
  return data.intro;
}

export async function getOracleStarters(userId: string): Promise<string[]> {
  const res = await authFetch(`${API_BASE}/oracle/starters?user_id=${encodeURIComponent(userId)}&_t=${Date.now()}`);
  if (!res.ok) throw new Error(`Oracle starters error: ${res.status}`);
  const data = await res.json();
  return data.starters;
}

export async function getOracleHistory(
  userId: string,
  conversationId?: string,
): Promise<{ role: string; content: string }[]> {
  const url = conversationId
    ? `${API_BASE}/oracle/history?user_id=${encodeURIComponent(userId)}&conversation_id=${encodeURIComponent(conversationId)}`
    : `${API_BASE}/oracle/history?user_id=${encodeURIComponent(userId)}`;
  const res = await authFetch(url);
  if (!res.ok) throw new Error(`Oracle history error: ${res.status}`);
  return res.json();
}

export async function deleteUserProfile(userId: string): Promise<{ deleted: number }> {
  const res = await authFetch(`${API_BASE}/user/delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  });
  if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
  return res.json();
}

export interface TransitData {
  transit_planet: string;
  natal_target: string;
  aspect: string;
  impact_score: number;
  date_range: string;
  start_date: string;
  end_date: string;
  duration_days: number;
  months_active: number;
  tone: string;
  keyword: string;
  description: string;
  guidance: string;
  retrograde_note: string;
  planet_energy: string;
  window_categories?: string[];
}

export interface WindowTransit {
  transit_planet: string;
  natal_target: string;
  aspect: string;
  date_range: string;
  start_date: string;
  end_date: string;
  duration_days: number;
  impact_score: number;
  tone: string;
}

export interface TransitForecast {
  transits: TransitData[];
  dominant_phase: string;
  phase_scores: Record<string, number>;
  months_analyzed: number;
  start_date: string;
  total_active_transits: number;
  window_map?: Record<string, WindowTransit[]>;
  mercury_retrogrades?: Array<{ start_date: string; end_date: string; duration_days: number; date_range: string }>;
  moon_sensitivity_windows?: Array<{ start_date: string; end_date: string; duration_days: number; date_range: string; aspect: string }>;
}

export async function getTransitForecast(userId: string, months: number = 12): Promise<TransitForecast> {
  const res = await authFetch(`${API_BASE}/transits?user_id=${encodeURIComponent(userId)}&months=${months}`);
  if (!res.ok) throw new Error(`Transit forecast error: ${res.status}`);
  return res.json();
}

// ═══════════════════════════════════════════
//  CIRCLE / SYNASTRY
// ═══════════════════════════════════════════

export interface CircleConnection {
  connection_id: string;
  name: string;
  relationship: string;
  birth_date: string;
  birth_time: string;
  birth_place: string;
  gender: string;
  time_unknown: boolean;
  chart: any;
  profile: any;
  created_at: number;
}

export type SynastryAxisKey = 'ease' | 'chemistry' | 'durability' | 'growth';

export interface EffectiveSynastryContact {
  from_body: string;
  to_body: string;
  aspect: string;
  orb: number;
  direction?: 'A_to_B' | 'B_to_A' | string;
  modifiers?: string[];
  aspect_group?: 'primary' | 'secondary' | string;
  effective_axes?: Partial<Record<SynastryAxisKey, number>>;
  effective_composite?: number;
  composite?: number;
}

export interface BondEvidence {
  planet1: string;
  planet2: string;
  aspect: string;
  orb: number;
  direction?: string;
  interpretation_id: string;
  interpretation_status: 'active' | 'needs_description' | 'no_v3_signature' | 'intentionally_excluded_by_v3_model';
  support_rank?: number | null;
  support_tier?: number | null;
  support_tier_name?: string | null;
  source_rank?: number | null;
  source_tier?: number | null;
  source_tier_name?: string | null;
  description: string;
  legacy_description: string;
  interpretation_source: 'tier_library' | 'v3_fallback';
  effective_composite: number;
  axis_contribution: {
    axis: SynastryAxisKey;
    value: number;
    nature: 'supportive' | 'challenging';
  };
  report_impact: {
    selection: 'supporting_evidence' | 'growth_edge';
    rank: number;
    effective_composite: number;
  };
  gift: string;
  shadow: string;
  integration: string;
}

export type BondDynamicsDimensionKey = 'physical_attraction' | 'relational_charge' | 'natural_flow' | 'volatility' | 'staying_power' | 'transformational_power';
export interface BondDynamicsEvidence {
  body_pair: [string, string]; aspect: string; orb: number; direction: string; contribution: number; reason: string;
}
export interface BondDynamicsDimension {
  style: 'quiet' | 'present' | 'pronounced' | 'dominant';
  intensity: number; peak_signature: number; breadth: number;
  evidence_count: number; omitted_evidence_count: number;
  support: { count: number; total: number }; challenge: { count: number; total: number };
  signed_balance: number; evidence: BondDynamicsEvidence[];
  gift: string; friction: string; practice: string;
}
export interface BondDynamics {
  version: string; status: 'experimental'; scale: 'relative_intensity_not_compatibility';
  dimensions: Record<BondDynamicsDimensionKey, BondDynamicsDimension>;
}

export interface SynastryResult {
  compatibility_score: number;
  bond_dynamics?: BondDynamics;
  model_hash?: string;
  engine_hash?: string;
  birth_time_complete?: boolean;
  percentile_status?: string;
  v3_axes?: Record<SynastryAxisKey, number>;
  contacts?: EffectiveSynastryContact[];
  effective_contacts?: EffectiveSynastryContact[];
  headline_score_version?: string;
  bond_profile?: {
    version: string;
    interpretation_catalog_hash: string;
    code: string;
    name: string;
    summary: string;
    dominant_axes: SynastryAxisKey[];
    growth_edge_axis: SynastryAxisKey;
    growth_edge: string;
    dimensions: Record<SynastryAxisKey, {
      percentile: number;
      band: 'pronounced' | 'present' | 'growth_edge';
    }>;
    confidence: {
      level: 'full' | 'reduced';
      label: string;
      description: string;
    };
    supporting_evidence: BondEvidence[];
    growth_edges: BondEvidence[];
  };
  v3_ranked_aspects?: Array<{
    planet1: string;
    planet2: string;
    aspect: string;
    orb: number;
    nature: string;
    strength: number;
    description: string;
    effective_group_weight: number;
    raw_composite: number;
    effective_composite: number;
  }>;
  v3_percentiles?: {
    chemistry: number;
    ease: number;
    durability: number;
    growth: number;
    composite: number;
  };
  tier: string;
  tier_description: string;
  harmony_score?: number;
  tension_score?: number;
  relationship_type?: string;
  algorithm_version?: string;
  dimensions?: {
    emotional_safety: number;
    communication: number;
    chemistry: number;
    stability: number;
    growth: number;
    shared_direction: number;
  };
  dimension_weights?: Record<string, number>;
  score_components?: Record<string, { score: number; weight: number }>;
  aspects: Array<{
    planet1: string;
    planet2: string;
    aspect: string;
    orb: number;
    nature: string;
    strength: number;
    description: string;
    tier_rank?: number;
    tier_num?: number;
    tier_name?: string;
  }>;
  element_compatibility?: { score: number; element1: string; element2: string };
  sun_compatibility?: number;
  moon_compatibility?: number;
  sun_moon_cross?: number;
  nodal_axis_compatibility?: {
    score: number;
    aspects: Array<{ node_person: string; node: 'North Node' | 'South Node' | 'Nodal Axis'; planet: string; aspect: string; orb: number; nature: string; weight: number; scoring_evidence?: number; description: string }>;
  };
  north_node_compatibility?: {
    score: number;
    aspects: Array<{ node_person: string; node?: 'North Node' | 'South Node' | 'Nodal Axis'; planet: string; aspect: string; orb: number; nature: string; weight: number; scoring_evidence?: number; description: string }>;
  };
  attribute_compatibility?: {
    overall: number;
    aligned: string[];
    complementary: string[];
    friction: string[];
    balanced?: string[];
    insights?: Array<{ attr: string; type: string; text: string }>;
  };
  archetype_dynamic?: {
    title: string;
    dynamic: string;
    strength: string;
    challenge: string;
  };
  strengths: string[];
  challenges: string[];
  person1: { name: string; archetype: string; sun_sign: string };
  person2: { name: string; sun_sign: string; moon_sign: string; rising_sign: string; archetype?: string };
}

export async function addCircleConnection(
  userId: string,
  data: {
    name: string;
    relationship: string;
    birth_date: string;
    birth_time?: string;
    birth_place?: string;
    latitude: number;
    longitude: number;
    time_unknown?: boolean;
    gender?: string;
  }
): Promise<{ connection: CircleConnection }> {
  const res = await authFetch(`${API_BASE}/circle/add`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, ...data }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Circle add error: ${res.status}`);
  }
  return res.json();
}

export async function removeCircleConnection(userId: string, connectionId: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/circle/remove`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, connection_id: connectionId }),
  });
  if (!res.ok) throw new Error(`Circle remove error: ${res.status}`);
}

export async function getCircleConnections(userId: string): Promise<CircleConnection[]> {
  const res = await authFetch(`${API_BASE}/circle/list?user_id=${encodeURIComponent(userId)}`);
  if (!res.ok) throw new Error(`Circle list error: ${res.status}`);
  const data = await res.json();
  return data.connections || [];
}

export async function getSynastryReport(userId: string, connectionId: string): Promise<SynastryResult> {
  const res = await authFetch(`${API_BASE}/circle/synastry?user_id=${encodeURIComponent(userId)}&connection_id=${encodeURIComponent(connectionId)}`);
  if (!res.ok) throw new Error(`Synastry error: ${res.status}`);
  const data = await res.json();
  return data.synastry;
}

export async function getSynastySynthesis(person1: string, person2: string, relationship: string, strengths: string[], challenges: string[], connectionId?: string, userId?: string): Promise<{ strengths: string; shadows: string }> {
  const res = await authFetch(`${API_BASE}/circle/synastry/synthesis`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ person1, person2, relationship, strengths, challenges, connection_id: connectionId || '', user_id: userId || '' }),
  });
  if (!res.ok) throw new Error(`Synthesis error: ${res.status}`);
  const data = await res.json();
  return data.synthesis;
}

// ═══════════════════════════════════════════
//  SHARE LINKS
// ═══════════════════════════════════════════

export interface ShareTeaser {
  person1: { name: string; sun_sign: string; moon_sign?: string; rising_sign?: string; archetype: string };
  person2: { name: string; sun_sign: string; moon_sign?: string; rising_sign?: string; archetype?: string };
  compatibility_score: number;
  tier: string;
  tier_description: string;
  relationship_type: string;
  top_aspects: { planet1: string; planet2: string; aspect: string; tier_name: string; tier_num: number | null }[];
}

export interface ShareResult {
  teaser: ShareTeaser;
  authenticated: boolean;
  full_synastry?: SynastryResult;
}

export async function createShareLink(userId: string, connectionId: string): Promise<{ share_id: string; share_url: string; og_url?: string }> {
  const res = await authFetch(`${API_BASE}/share/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, connection_id: connectionId }),
  });
  if (!res.ok) throw new Error(`Share create error: ${res.status}`);
  return res.json();
}

export async function getShareData(shareId: string, viewerId?: string): Promise<ShareResult> {
  let url = `${API_BASE}/share/get?id=${encodeURIComponent(shareId)}`;
  if (viewerId) url += `&viewer_id=${encodeURIComponent(viewerId)}`;
  // Public viewers remain anonymous; signed-in owners send their Cognito token
  // so the backend can derive ownership instead of trusting viewer_id.
  const res = await authFetch(url);
  if (!res.ok) throw new Error(`Share get error: ${res.status}`);
  return res.json();
}


// ── Soul Essence System ──

export async function getEssenceStatus(userId: string) {
  const res = await authFetch(`${API_BASE}/essence/status?userId=${userId}`);
  return res.json();
}

export async function earnEssence(userId: string, action: string) {
  const res = await authFetch(`${API_BASE}/essence/earn`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, action }),
  });
  return res.json();
}

export async function spendEssence(userId: string, amount: number, reason: string) {
  const res = await authFetch(`${API_BASE}/essence/spend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, amount, reason }),
  });
  return res.json();
}

export async function saveSpread(userId: string, spreadType: string, intention: string, cards: any[], circleName?: string, circleRelationship?: string): Promise<{ spread_id: string }> {
  const res = await authFetch(`${API_BASE}/tarot/spread/save`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, spread_type: spreadType, intention, cards, circle_name: circleName || '', circle_relationship: circleRelationship || '' }),
  });
  return res.json();
}

export async function getSpreads(userId: string): Promise<any[]> {
  const res = await authFetch(`${API_BASE}/tarot/spread/list?userId=${encodeURIComponent(userId)}`);
  const data = await res.json();
  return data.spreads || [];
}

export async function claimDailyBonus(userId: string) {
  const res = await authFetch(`${API_BASE}/essence/daily-bonus`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId }),
  });
  return res.json();
}

export async function generateTrial(userId: string, attribute: string, tier: string) {
  const res = await authFetch(`${API_BASE}/essence/trial/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, attribute, tier }),
  });
  return res.json();
}

export async function submitTrial(userId: string, trialId: string, answers: number[]) {
  const res = await authFetch(`${API_BASE}/essence/trial/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, trialId, answers }),
  });
  return res.json();
}

export async function purchaseReward(userId: string, reward: string, extra?: Record<string, string>) {
  const res = await authFetch(`${API_BASE}/essence/rewards/purchase`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, reward, ...extra }),
  });
  return res.json();
}

export async function getActiveRewards(userId: string) {
  const res = await authFetch(`${API_BASE}/essence/rewards?user_id=${encodeURIComponent(userId)}`);
  return res.json();
}

// ── Daily Pulse ──
export interface DailyPulse {
  date: string;
  moon: {
    sign: string;
    degree: string;
    phase: string;
    illumination: number;
    attribute: string;
    user_score: number;
    message: string;
  };
  transit: {
    planet: string;
    event: string;
    sign: string;
    attribute: string;
    user_score: number;
    theme: string;
    message: string;
  };
  circle: {
    has_member: boolean;
    member_name: string | null;
    relationship: string | null;
    message: string;
  };
  timing?: {
    has_blueprint: boolean;
    teaser?: boolean;
    message: string;
    active_transit?: {
      transit_planet: string;
      natal_target: string;
      aspect: string;
      impact_score: number;
      description: string;
      date_range: string;
      tone: string;
      keyword: string;
    } | null;
    boost?: {
      tier: string;
      label: string;
      primary_attr: string;
      primary_boost: number;
      secondary_attr: string;
      secondary_boost: number;
      primary_score: number;
      secondary_score: number;
    };
  };
}

export async function getDailyPulse(userId: string): Promise<DailyPulse | null> {
  const res = await authFetch(`${API_BASE}/today/pulse?user_id=${encodeURIComponent(userId)}&_t=${Date.now()}`, {
    cache: 'no-store',
  });
  if (!res.ok) return null;
  return res.json();
}

export async function applyTimingBoost(userId: string): Promise<any> {
  const res = await authFetch(`${API_BASE}/pulse/timing-boost`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  });
  return res.json();
}

export async function applyGrowthSignal(userId: string, attribute: string, amount: number = 0.1, source: string = 'reflection'): Promise<any> {
  try {
    const res = await authFetch(`${API_BASE}/growth/signal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, attribute, amount, source }),
    });
    return res.json();
  } catch {
    return { error: 'Growth signal failed' };
  }
}

// --- Premium relationship story ---
export type StoryConfidence = 'birth-time independent' | 'birth-time enhanced' | 'exact-time required';
export interface RelationshipStoryEvidence { id: string; kind: string; summary: string; confidence: StoryConfidence; rank: number; tier_rank?: number; tier_multiplier?: number; orb?: number; }
export interface RelationshipStoryChapter { id: string; title: string; body: string; source_ids: string[]; }
export interface RelationshipStoryReport {
  version: string; fingerprint: string; relationship_type: string; generated_at: number;
  status: 'complete'; title: string; subtitle: string; model: string; word_count: number;
  archetype?: { name?: string; description?: string };
  chapters: RelationshipStoryChapter[];
  dimensions: Array<{id:string;title:string;score:number}>;
  evidence: RelationshipStoryEvidence[];
  share_excerpt?: string;
}
export interface RelationshipStoryResult {
  status: 'not_started' | 'generating' | 'complete' | 'failed';
  report?: RelationshipStoryReport;
  progress?: number;
  stage?: 'queued' | 'writing';
  started_at?: number;
  error?: string;
}
export async function getRelationshipStory(userId: string, connectionId: string, start = false): Promise<RelationshipStoryResult> {
  const startParam = start ? '&start=1' : '';
  const res = await authFetch(`${API_BASE}/circle/story?user_id=${encodeURIComponent(userId)}&connection_id=${encodeURIComponent(connectionId)}${startParam}`);
  const data = await res.json();
  if (!res.ok) { const error = new Error(data.error || 'Story unavailable'); (error as any).status = res.status; throw error; }
  return {
    status: data.status || (data.report ? 'complete' : 'not_started'),
    report: data.report,
    progress: data.progress,
    stage: data.stage,
    started_at: data.started_at,
    error: data.error,
  };
}

// --- Stripe Subscription ---

export interface SubscriptionStatus {
  tier: 'free' | 'premium';
  status: string;
  plan?: string;
  trial_end?: number | null;
  current_period_end?: number;
  cancel_at_period_end?: boolean;
}

export async function getSubscriptionStatus(userId: string): Promise<SubscriptionStatus> {
  const res = await authFetch(`${API_BASE}/stripe/subscription?user_id=${encodeURIComponent(userId)}`);
  const data = await res.json();
  if (!res.ok) return { tier: 'free', status: 'none' };
  return data;
}

/**
 * Sync IAP purchases (premium + blueprints) from RevenueCat to the backend.
 * Server verifies with RevenueCat directly — client is never trusted.
 * Call after any successful purchase/restore so server-side gates
 * (oracle/cosmic pulse/circle limits, blueprints) unlock.
 */
export async function syncIAP(userId: string): Promise<{ premium: boolean; plan: string | null; blueprints: string[] }> {
  const res = await authFetch(`${API_BASE}/iap/sync`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'IAP sync failed');
  return data;
}

export async function createCheckoutSession(userId: string, email: string, plan: 'monthly' | 'yearly' = 'monthly'): Promise<string> {
  const res = await authFetch(`${API_BASE}/stripe/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, email, plan }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Checkout failed');
  return data.url;
}

export async function createPortalSession(userId: string): Promise<string> {
  const res = await authFetch(`${API_BASE}/stripe/portal`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Portal failed');
  return data.url;
}

// Blueprint purchases
export async function getBlueprintStatus(userId: string): Promise<{ owned: string[]; source: string }> {
  const res = await authFetch(`${API_BASE}/blueprint/status?user_id=${encodeURIComponent(userId)}`);
  return res.json();
}

export async function createBlueprintCheckout(userId: string, email: string, report: string): Promise<string> {
  const res = await authFetch(`${API_BASE}/blueprint/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, email, report }),
  });
  const data = await res.json();
  if (data.already_owned) return 'already_owned';
  if (!res.ok) throw new Error(data.error || 'Checkout failed');
  return data.url;
}

export async function markTutorialSeen(userId: string): Promise<void> {
  await authFetch(`${API_BASE}/tutorial/seen`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  });
}

export interface BoostSource {
  type: 'moon' | 'transit';
  label: string;
  amount: number;
  temporary: boolean;
  end_date?: string;
}

export interface AttrBoost {
  total: number;
  sources: BoostSource[];
}

export interface PermanentGrowth {
  total: number;
  sources: { type: string; label: string; amount: number; permanent: boolean }[];
}

export interface ActiveBoostsResponse {
  boosts: Record<string, AttrBoost>;
  permanent: Record<string, PermanentGrowth>;
  moon: { sign: string; phase: string; phase_name: string };
}

export async function getActiveBoosts(userId: string): Promise<ActiveBoostsResponse> {
  try {
    const res = await authFetch(`${API_BASE}/profile/active-boosts?user_id=${encodeURIComponent(userId)}`);
    if (!res.ok) return { boosts: {}, permanent: {}, moon: { sign: '', phase: '', phase_name: '' } };
    return res.json();
  } catch {
    return { boosts: {}, permanent: {}, moon: { sign: '', phase: '', phase_name: '' } };
  }
}
